import type { CSSProperties, ReactNode } from 'react';
import { Button } from '../../components/Button';
import { PS, T } from '../../lib/theme';
import { formatLocal } from '../../lib/time';
import type {
  TtarParticipantEnrollmentState,
  TtarTournamentSyncResponse,
  TtarTournamentSyncState,
} from '../../lib/types';

interface Props {
  result: TtarTournamentSyncResponse | null;
  loading: boolean;
  error: Error | null;
  retrying: boolean;
  onRetry: () => void;
  onRefresh: () => void;
  /** user_id → display name, from the roster; unknown ids show the raw id. */
  playerNames?: Record<string, string>;
  /** user_id of the enrollment retry in flight, if any. */
  retryingEnrollment?: string | null;
  onRetryEnrollment?: (userId: string) => void;
}

/**
 * Admin-only TTAR state. The provider URL remains server-managed: the display
 * endpoint releases it only during the local tournament window, so this panel
 * deliberately has no URL field to paste, edit, or construct in the browser.
 */
export function TournamentTtarSync({
  result,
  loading,
  error,
  retrying,
  onRetry,
  onRefresh,
  playerNames = {},
  retryingEnrollment = null,
  onRetryEnrollment,
}: Props) {
  if (loading) {
    return <div style={{ ...T.caption, color: PS.textSec }}>Loading TTAR synchronization…</div>;
  }

  if (error) {
    return (
      <section style={cardStyle} aria-label="TTAR synchronization">
        <h2 style={{ ...T.subheading, margin: 0 }}>TTAR synchronization</h2>
        <Row label="State" value="Unavailable" />
        <p style={{ ...T.caption, color: PS.errorText, margin: 0 }}>{error.message}</p>
        <p style={warningStyle} role="alert">
          Load the TTAR synchronization state before retrying. A previous ambiguous create may have left a duplicate at
          TTAR; clean up any duplicate manually.
        </p>
        <div>
          <Button variant="secondary" onClick={onRefresh}>
            Reload TTAR state
          </Button>
        </div>
      </section>
    );
  }

  if (!result) return null;

  const { sync } = result;
  const retryAvailable = sync.status === 'not_started' || sync.status === 'pending' || sync.status === 'failed';
  // A failed create may be an ambiguous upstream outcome even when the
  // provider has not supplied a stronger duplicate signal. Warn before every
  // staff retry in either case; cleanup remains a manual operator action.
  const showDuplicateWarning =
    sync.possible_duplicate_warning ||
    Boolean(sync.duplicate_warning) ||
    sync.status === 'failed' ||
    sync.status === 'retry_exhausted';

  return (
    <section style={cardStyle} aria-label="TTAR synchronization">
      <h2 style={{ ...T.subheading, margin: 0 }}>TTAR synchronization</h2>
      <Row label="State" value={<StateBadge state={sync} />} />
      <Row label="TTAR reference" value={sync.remote_tournament_id == null ? 'Not assigned' : `#${sync.remote_tournament_id}`} />
      <Row label="Create attempts" value={String(sync.create_attempts)} />
      <Row
        label="Last attempt"
        value={sync.last_attempt_at ? formatLocal(sync.last_attempt_at, 'yyyy-MM-dd HH:mm') : 'Not attempted'}
      />
      {sync.last_error && <Row label="Safe provider error" value={sync.last_error} />}
      {sync.remote_tournament_id != null && (
        <Row label="Provider URL" value="Provider-managed for the booking window — no manual URL entry is needed." />
      )}

      {showDuplicateWarning && (
        <p style={warningStyle} role="alert">
          {sync.duplicate_warning ?? 'A retry may leave a duplicate at TTAR; clean up any duplicate manually.'}
        </p>
      )}

      {sync.status === 'retry_exhausted' ? (
        <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
          Retry limit reached. The provider retry bound is exhausted.
        </p>
      ) : retryAvailable ? (
        <div>
          <Button disabled={retrying} onClick={onRetry}>
            {retrying ? 'Retrying…' : 'Retry TTAR sync'}
          </Button>
        </div>
      ) : null}

      {result.enrollments.length > 0 && (
        <EnrollmentList
          enrollments={result.enrollments}
          playerNames={playerNames}
          retryingEnrollment={retryingEnrollment}
          onRetryEnrollment={onRetryEnrollment}
        />
      )}
    </section>
  );
}

/**
 * Per-player TTAR enrollment. The API retries `pending`/`failed` rows from cron
 * until each row's attempt limit; a Retry here resets that limit, so it is
 * offered only where automatic retries have failed or run out.
 */
function EnrollmentList({
  enrollments,
  playerNames,
  retryingEnrollment,
  onRetryEnrollment,
}: {
  enrollments: TtarParticipantEnrollmentState[];
  playerNames: Record<string, string>;
  retryingEnrollment: string | null;
  onRetryEnrollment?: (userId: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <h3 id="ttar-enrollments-heading" style={{ ...T.label, margin: 0 }}>
        Player enrollments
      </h3>
      <ul aria-labelledby="ttar-enrollments-heading" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {enrollments.map((row) => {
          const name = playerNames[row.user_id] ?? row.user_id;
          const { label, color } = enrollmentPresentation(row.status);
          const retryable = row.status === 'failed' || row.status === 'retry_exhausted';
          const isRetrying = retryingEnrollment === row.user_id;
          return (
            <li key={row.user_id} style={enrollmentRowStyle}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ ...T.ui, color: PS.nearBlack, overflowWrap: 'anywhere' }}>{name}</span>
                <span style={{ ...T.microLabel, color }}>{label}</span>
                <span style={{ ...T.caption, color: PS.textSec }}>
                  {row.attempts} attempt{row.attempts === 1 ? '' : 's'}
                </span>
                {retryable && onRetryEnrollment && (
                  <Button
                    variant="secondary"
                    disabled={retryingEnrollment !== null}
                    aria-label={`${isRetrying ? 'Retrying' : 'Retry'} ${name}`}
                    onClick={() => onRetryEnrollment(row.user_id)}
                  >
                    {isRetrying ? 'Retrying…' : 'Retry'}
                  </Button>
                )}
              </div>
              {row.last_error && (
                <div style={{ ...T.caption, color: PS.errorText, overflowWrap: 'anywhere' }}>{row.last_error}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function enrollmentPresentation(status: TtarParticipantEnrollmentState['status']) {
  switch (status) {
    case 'enrolled':
      return { label: 'Enrolled', color: PS.success };
    case 'failed':
      return { label: 'Failed', color: PS.danger };
    case 'retry_exhausted':
      return { label: 'Retry limit reached', color: PS.danger };
    case 'enrolling':
      return { label: 'Enrolling', color: PS.info };
    case 'pending':
    default:
      return { label: 'Pending', color: PS.pendingText };
  }
}

function StateBadge({ state }: { state: TtarTournamentSyncState }) {
  const { label, color } = syncStatePresentation(state.status);
  return (
    <span
      style={{
        ...T.microLabel,
        color,
        border: `1px solid ${color}`,
        borderRadius: 3,
        padding: '2px 8px',
      }}
    >
      {label}
    </span>
  );
}

function syncStatePresentation(status: TtarTournamentSyncState['status']) {
  switch (status) {
    case 'synced':
      return { label: 'Synced', color: PS.success };
    case 'failed':
      return { label: 'Failed', color: PS.danger };
    case 'retry_exhausted':
      return { label: 'Retry limit reached', color: PS.danger };
    case 'creating':
      return { label: 'Creating', color: PS.info };
    case 'pending':
      return { label: 'Pending', color: PS.pendingText };
    case 'not_started':
    default:
      return { label: 'Not started', color: PS.textSec };
  }
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <div style={{ ...T.label, width: 160, flexShrink: 0 }}>{label}</div>
      <div style={{ ...T.ui, color: PS.nearBlack, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

const cardStyle: CSSProperties = {
  background: PS.white,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const enrollmentRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '6px 0',
  borderTop: `1px solid ${PS.borderLight}`,
};

const warningStyle: CSSProperties = {
  ...T.caption,
  color: PS.errorText,
  background: PS.errorBg,
  border: `1px solid ${PS.errorBorder}`,
  borderRadius: 3,
  padding: 10,
  margin: 0,
};
